import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../../common/types';
import { ErrorView } from '../../../../common/views/ErrorView/ErrorView';
import { useGetReelsQuery } from '../../../../../redux/api/fymAPI';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { aiReelStudioParams } from '../../studioRoute';
import { Panel, PanelHead } from '../../../components/primitives/Panel';
import { reelReviewMessages } from './reelReviewMessages';
import { ReelHistoryCard } from './ReelHistoryCard';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the preview, the history and the sheets */
import styles from './ReelReview.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReelHistoryStripProps {
  accountId: number;
  catalogItemUuid: string;
  /** The reel on screen; its card has the ring. */
  currentUuid: string;
  releaseTitle?: string;
}

/**
 * The reels of one release (design c1): newest first (the API sorts them, 20 at most), a status
 * pill each. A click opens that reel (`?reel=`).
 */
const ReelHistoryStripComponent: React.FC<ReelHistoryStripProps> = (props) => {
  const { accountId, catalogItemUuid, currentUuid, releaseTitle } = props;
  const intl = useIntl();
  const router = useSMLRouter();
  const { data, error, refetch } = useGetReelsQuery({ accountId, catalogItemUuid });

  const open = useCallback(
    (uuid: string) => router.push(SMLRoutes.promoteStudio, aiReelStudioParams({ catalogItemUuid, reel: uuid })),
    [catalogItemUuid, router]
  );
  const retry = useCallback(() => {
    refetch().catch(console.error);
  }, [refetch]);

  const cards = useMemo(
    () =>
      (data?.reels ?? []).map((reel) => (
        <ReelHistoryCard key={reel.uuid} reel={reel} isCurrent={reel.uuid === currentUuid} onOpen={open} />
      )),
    [currentUuid, data?.reels, open]
  );

  if (error === undefined && cards.length === 0) {
    return null;
  }

  return (
    <Panel>
      <PanelHead
        title={
          releaseTitle === undefined
            ? intl.formatMessage(reelReviewMessages.historyTitleNoRelease)
            : intl.formatMessage(reelReviewMessages.historyTitle, { release: releaseTitle })
        }
        right={<span className={styles.hdate}>{intl.formatMessage(reelReviewMessages.historyOrder)}</span>}
      />
      {error !== undefined ? <ErrorView error={error} retry={retry} /> : <ul className={styles.hist}>{cards}</ul>}
    </Panel>
  );
};

export const ReelHistoryStrip = TypedMemo(ReelHistoryStripComponent);
